'use client';

import React from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import Container from '@mui/material/Container';
import Footer from '@/components/footer';
import BeforeLoginHeader from '@/components/layout-new/before-login/Header';
import Header from '@/components/layout-new/Header';
import DashboardFooter from '@/components/layout-new/Footer';
import DashboardNav from '@/components/layout-new/dashboard/DashboardNav';
import { AuthContext } from '@/context/create-context';
import { HEADER_ID, RESUME_DASHBOARD_NAV_ID } from '@/constants/component-id';

gsap.registerPlugin(useGSAP);

const Main = (props: {
  children: React.ReactNode;
  isHideHeader?: boolean;
  outContainerEle?: React.ReactElement;
  isSalaryLink?: boolean;
}) => {
  const {
    children,
    isHideHeader,
    outContainerEle,
    isSalaryLink = true,
  } = props;
  const { user } = React.useContext(AuthContext);

  const isLogin = React.useMemo(() => !!user?.is_authenticated, [user]);

  useGSAP(() => {
    if (!isHideHeader || !user) return;
    const animateElement = (id: string, display: string) => {
      const element = document.getElementById(id);
      if (element) {
        gsap.set(`#${id}`, { display });
        gsap.from(`#${id}`, {
          opacity: 0,
          duration: 0.3,
          ease: 'power1.inOut',
        });
        return true;
      }
      return false;
    };

    const waitForElements = () => {
      if (isLogin) {
        const headerAnimated = animateElement(HEADER_ID, 'flex');
        const navAnimated = animateElement(RESUME_DASHBOARD_NAV_ID, 'block');
        if (!headerAnimated || !navAnimated) {
          requestAnimationFrame(waitForElements);
        }
      } else {
        if (!animateElement(HEADER_ID, 'flex')) {
          requestAnimationFrame(waitForElements);
        }
      }
    };
    requestAnimationFrame(waitForElements);
  }, [isLogin, user]);

  return (
    <>
      <div className={isLogin ? 'pt-16 tablet:flex tablet:pt-0' : 'pt-16'}>
        {isLogin ? (
          <>
            <div className="tablet:hidden">
              <Header showLogo={true} isHideHeader={isHideHeader} />
            </div>
            <DashboardNav
              isSalaryLink={isSalaryLink}
              isHideHeader={isHideHeader}
            />
          </>
        ) : (
          <BeforeLoginHeader isSalaryLink={true} isHideHeader={isHideHeader} />
        )}
        <main
          className={
            isLogin
              ? 'min-h-[calc(100vh-22px)] w-full pb-7'
              : 'min-h-screen w-full pb-7'
          }
        >
          {outContainerEle}
          <Container
            className={isLogin ? '' : '!min-h-screen !mt-3.5 tablet:!mt-1'}
            maxWidth="desktop"
          >
            {children}
          </Container>
        </main>
      </div>
      {isLogin ? <DashboardFooter /> : <Footer />}
    </>
  );
};

export default Main;
